import { useEffect, useRef, useState } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";
import { cn } from "../../utils/utils";
import useBrandTranslation from "../../hooks/useBrandTranslation";
import { useAuth } from "../../lib/AuthContext";
import { useUpdateUserMutation } from "../../apis/api.users";

const LANGUAGES = [
  { code: "es", labelKey: "common.spanish", defaultLabel: "Español" },
  { code: "en", labelKey: "common.english", defaultLabel: "English" },
];

export default function LanguageSwitcher({ className, variant = "light" }) {
  const { language, setLanguage, isMultilingual, t } = useBrandTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  if (!isMultilingual) {
    return null;
  }

  const isLight = variant === "light";
  const currentLanguage = LANGUAGES.find((lng) => lng.code === language);

  const handleLanguageChange = async (lng) => {
    await setLanguage(lng);
    setOpen(false);
    if (user?.id) {
      try {
        await useUpdateUserMutation(user.id, { preferred_locale: lng });
      } catch (err) {
        console.warn("Could not save locale preference:", err);
      }
    }
  };

  return (
    <div className={cn("relative", className)} ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          "flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold transition-colors",
          isLight
            ? "text-white hover:bg-white/15"
            : "text-gray-600 hover:bg-gray-100"
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("common.language", "Idioma")}
      >
        <Globe
          className={cn(
            "h-4 w-4 shrink-0",
            isLight ? "text-white/90" : "text-gray-500"
          )}
        />
        <span>
          {t(
            currentLanguage?.labelKey,
            currentLanguage?.defaultLabel || language
          )}
        </span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 transition-transform",
            open && "rotate-180",
            isLight ? "text-white/80" : "text-gray-400"
          )}
        />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-2 min-w-[9rem] overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          role="listbox"
          aria-label={t("common.language", "Idioma")}
        >
          {LANGUAGES.map((lng) => {
            const isSelected = language === lng.code;
            return (
              <button
                key={lng.code}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => handleLanguageChange(lng.code)}
                className={cn(
                  "flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors",
                  isSelected
                    ? "bg-blue-50 font-medium text-blue-700"
                    : "text-gray-700 hover:bg-gray-50"
                )}
              >
                <span>{t(lng.labelKey, lng.defaultLabel)}</span>
                {isSelected && <Check className="h-4 w-4 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
